import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  APIProvider,
  Map,
  MapControl,
  ControlPosition,
  AdvancedMarker,
  useMap,
} from "@vis.gl/react-google-maps";
import Supercluster from "supercluster";
import WelocateHeader from "./WelocateHeader";
import WelocateSidebar from "./WelocateSidebar";
import WelocateFilters from "./WelocateFilters";
import WelocateToolbox from "./WelocateToolbox";
import WelocateCompareModal from "./WelocateCompareModal";
import WelocateCompareSideBySide from "./WelocateCompareSideBySide";
import useHttpNoAuth from "../../hooks/use-http-no-auth";
import WelocateMarker from "./WelocateMarker";
import { ReactComponent as MarkerBlueIcon } from "../../img/icons/marker-blue.svg";
import { ReactComponent as MarkerOrangeIcon } from "../../img/icons/marker-orange.svg";
import { ReactComponent as MarkerGreyIcon } from "../../img/icons/marker-grey.svg";
import { ReactComponent as MarkerGreenIcon } from "../../img/icons/marker-green.svg";
import "./WelocateListings.css";

const API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  "AIzaSyD-Q4bAwz9FlOk3egs1r7QEfm-iSgrqFlc";
const MAP_ID =
  process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || "7623dc388ac6d208c8089b8e";
const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000/api/v1";

const STATUS_COLORS = {
  available: "#1e3a6e",
  sale_pending: "#E27C00",
  sold: "#9CA3AF",
};

const MarkersLayer = ({
  clusters,
  scIndex,
  selectedListing,
  onSelectListing,
  activeStatuses,
}) => {
  const clusterColor =
    activeStatuses?.length === 1
      ? STATUS_COLORS[activeStatuses[0]] ?? "#1e3a6e"
      : "#1e3a6e";
  const map = useMap();

  const handleClusterClick = useCallback(
    (cluster) => {
      const [lng, lat] = cluster.geometry.coordinates;
      const expansionZoom = Math.min(
        scIndex.getClusterExpansionZoom(cluster.id),
        20
      );
      map.setZoom(expansionZoom);
      map.panTo({ lat, lng });
    },
    [map, scIndex]
  );

  return clusters.map((cluster) => {
    const [lng, lat] = cluster.geometry.coordinates;
    const { cluster: isCluster, point_count, listing } = cluster.properties;

    if (isCluster) {
      return (
        <AdvancedMarker
          key={`cluster-${cluster.id}`}
          position={{ lat, lng }}
          onClick={() => handleClusterClick(cluster)}
        >
          <div
            className="wl-cluster-marker"
            style={{ background: clusterColor }}
          >
            {point_count}
          </div>
        </AdvancedMarker>
      );
    }

    return (
      <AdvancedMarker
        key={listing.id ?? `${lat}-${lng}`}
        position={{ lat, lng }}
        onClick={() => onSelectListing(listing)}
      >
        <WelocateMarker
          isSelected={selectedListing === listing}
          isAssigned={listing.is_assigned}
          soldStatus={listing.sold_status}
        />
      </AdvancedMarker>
    );
  });
};

const MapController = ({ selectedListing, selectedCity }) => {
  const map = useMap();

  useEffect(() => {
    if (selectedListing && map) {
      map.panTo({ lat: selectedListing.lat, lng: selectedListing.lng });
    }
  }, [selectedListing, map]);

  useEffect(() => {
    if (
      selectedCity &&
      map &&
      selectedCity.lat != null &&
      selectedCity.lng != null
    ) {
      map.panTo({ lat: selectedCity.lat, lng: selectedCity.lng });
      map.setZoom(12);
    }
  }, [selectedCity, map]);

  return null;
};

const LEGEND_CONFIG = [
  {
    filterStatus: "available",
    label: "Available",
    Icon: MarkerBlueIcon,
    match: (s) => s === "for_sale" || s === "available",
  },
  {
    filterStatus: "sale_pending",
    label: "Sale pending",
    Icon: MarkerOrangeIcon,
    match: (s) => s === "under_bid" || s === "under_option",
  },
  {
    filterStatus: "sold",
    label: "Sold",
    Icon: MarkerGreyIcon,
    match: (s) => s === "sold" || s === "sold_under_reservation",
  },
  {
    filterStatus: "",
    label: "Currenty selected",
    Icon: MarkerGreenIcon,
    match: (s) => {},
  },
];

const MapLegend = ({ listings, activeStatuses, onToggleStatus }) => {
  const counts = useMemo(
    () =>
      LEGEND_CONFIG.reduce((acc, { filterStatus, match }) => {
        acc[filterStatus] = listings.filter((l) => match(l.sold_status)).length;
        return acc;
      }, {}),
    [listings]
  );

  return (
    <div className="wl-legend">
      <span className="wl-legend-title">LEGEND</span>
      <div className="wl-legend-divider" />
      {LEGEND_CONFIG.map(({ filterStatus, label, Icon }) => {
        // const inactive = activeStatuses.length > 0 && !activeStatuses.includes(filterStatus);
        return (
          <button
            key={filterStatus}
            className={`wl-legend-item`}
            // onClick={() => onToggleStatus(filterStatus)}
          >
            <Icon className="wl-legend-icon" />
            <span className="wl-legend-label">{label}</span>
          </button>
        );
      })}
    </div>
  );
};

const ClusteredMarkers = ({
  listings,
  selectedListing,
  onSelectListing,
  activeStatuses,
  onToggleStatus,
  selectedCity,
}) => {
  const [mapBounds, setMapBounds] = React.useState(null);
  const [mapZoom, setMapZoom] = React.useState(10);

  const scIndex = useMemo(() => {
    const index = new Supercluster({ radius: 60, maxZoom: 16 });
    index.load(
      listings.map((l) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [l.lng, l.lat] },
        properties: { listing: l },
      }))
    );
    return index;
  }, [listings]);

  const clusters = useMemo(() => {
    if (!mapBounds) return [];
    return scIndex.getClusters(
      [mapBounds.west, mapBounds.south, mapBounds.east, mapBounds.north],
      Math.round(mapZoom)
    );
  }, [scIndex, mapBounds, mapZoom]);

  const handleBoundsChanged = useCallback((e) => {
    setMapBounds(e.detail.bounds);
    setMapZoom(e.detail.zoom);
  }, []);

  return (
    <Map
      style={{ width: "100%", height: "100%" }}
      defaultCenter={{ lat: 52.3676, lng: 4.9041 }}
      defaultZoom={10}
      mapId={MAP_ID}
      gestureHandling="greedy"
      disableDefaultUI={true}
      clickableIcons={false}
      onBoundsChanged={handleBoundsChanged}
    >
      <MapControl position={ControlPosition.TOP_RIGHT}>
        <div style={{ margin: "14px 14px 0 0" }}>
          <span className="wl-count-pill">
            {listings.length} properties on map
          </span>
        </div>
      </MapControl>
      <MapControl position={ControlPosition.BOTTOM_LEFT}>
        <div style={{ margin: "0 0 14px -56px" }}>
          <MapLegend
            listings={listings}
            activeStatuses={activeStatuses}
            onToggleStatus={onToggleStatus}
          />
        </div>
      </MapControl>
      <MapController
        selectedListing={selectedListing}
        selectedCity={selectedCity}
      />
      <MarkersLayer
        clusters={clusters}
        scIndex={scIndex}
        selectedListing={selectedListing}
        onSelectListing={onSelectListing}
        activeStatuses={activeStatuses}
      />
    </Map>
  );
};

const WelocateListings = () => {
  const [listings, setListings] = React.useState([]);
  const [listingCounts, setListingCounts] = React.useState(null);
  const [selectedListing, setSelectedListing] = React.useState(null);
  const [selectionSource, setSelectionSource] = useState(null);
  const [expandRequest, setExpandRequest] = useState(0);
  const [collapseRequest, setCollapseRequest] = useState(0);
  const [aiOpenRequest, setAiOpenRequest] = useState(0);
  const [selectedCity, setSelectedCity] = React.useState(null);
  const [listingMode, setListingMode] = React.useState("my");
  const [uxExperience, setUxExperience] = React.useState(1);
  const [bedroomFilter, setBedroomFilter] = useState(null);
  const [areaFilter, setAreaFilter] = useState(null);
  const [typeFilter, setTypeFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [energyLabelsFilter, setEnergyLabelsFilter] = useState([]);
  const [compareList, setCompareList] = useState([]);
  const [isSideBySideOpen, setIsSideBySideOpen] = useState(false);

  const MAX_COMPARE = 3;
  const compareKey = (l) => `${l?.lat}-${l?.lng}`;

  const addToCompare = useCallback((listing) => {
    if (!listing) return;
    setCompareList((prev) => {
      if (prev.length >= MAX_COMPARE) return prev;
      const key = compareKey(listing);
      if (prev.some((l) => compareKey(l) === key)) return prev;
      return [...prev, listing];
    });
  }, []);

  const removeFromCompare = useCallback((listing) => {
    const key = compareKey(listing);
    setCompareList((prev) => prev.filter((l) => compareKey(l) !== key));
  }, []);

  const clearCompare = useCallback(() => setCompareList([]), []);

  const selectListingExpanded = useCallback((listing) => {
    setSelectedListing(listing);
    setSelectionSource("ai");
    setExpandRequest((n) => n + 1);
  }, []);

  const selectListingFromMap = useCallback((listing) => {
    setSelectedListing(listing);
    setSelectionSource(listing ? "map" : null);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedListing(null);
    setSelectionSource(null);
  }, []);

  const backToAi = useCallback(() => {
    setCollapseRequest((n) => n + 1);
    setAiOpenRequest((n) => n + 1);
  }, []);

  const { sendRequest: getListingsRequest } = useHttpNoAuth();

  const {
    sendRequest: getListingCountsRequest,
    isLoading: countsLoading,
    error: countsError,
  } = useHttpNoAuth();

  const listingModeRef = React.useRef("my");
  const activeFilters = React.useRef({
    minPrice: null,
    maxPrice: null,
    types: [],
    bedrooms: null,
    minArea: null,
    statuses: [],
    energyLabels: [],
  });

  const refetch = () => {
    // refetch listings
    getListings(activeFilters.current);
    getListingCounts();
  };

  const getListings = useCallback(
    (filters) => {
      const body = {};
      if (filters?.minPrice != null) body.min_price = filters.minPrice;
      if (filters?.maxPrice != null) body.max_price = filters.maxPrice;
      if (filters?.types?.length) body.types = filters.types;
      if (filters?.bedrooms != null) body.bedrooms = filters.bedrooms;
      if (filters?.minArea != null) body.min_area = filters.minArea;
      if (filters?.statuses?.length) body.statuses = filters.statuses;
      if (filters?.energyLabels?.length)
        body.energy_labels = filters.energyLabels;
      body.is_assigned = listingModeRef.current === "my";
      getListingsRequest(
        {
          url: `${BACKEND_URL}/realmex/get_listings`,
          method: "POST",
          body,
        },
        (data) => setListings(data)
      );
    },
    [getListingsRequest]
  );

  const getListingCounts = useCallback(() => {
    getListingCountsRequest(
      { url: `${BACKEND_URL}/realmex/listing_counts`, method: "GET" },
      (data) => setListingCounts(data)
    );
  }, [getListingCountsRequest]);

  useEffect(() => {
    getListings();
    getListingCounts();
  }, [getListings, getListingCounts]);

  const handlePriceApply = useCallback(
    (min, max) => {
      activeFilters.current = {
        ...activeFilters.current,
        minPrice: min,
        maxPrice: max,
      };
      getListings(activeFilters.current);
    },
    [getListings]
  );

  const handleTypeApply = useCallback(
    (types) => {
      setTypeFilter(types);
      activeFilters.current = { ...activeFilters.current, types };
      getListings(activeFilters.current);
    },
    [getListings]
  );

  const handleBedroomApply = useCallback(
    (bedrooms) => {
      setBedroomFilter(bedrooms);
      activeFilters.current = { ...activeFilters.current, bedrooms };
      getListings(activeFilters.current);
    },
    [getListings]
  );

  const handleAreaApply = useCallback(
    (minArea) => {
      setAreaFilter(minArea);
      activeFilters.current = { ...activeFilters.current, minArea };
      getListings(activeFilters.current);
    },
    [getListings]
  );

  const handleStatusApply = useCallback(
    (statuses) => {
      setStatusFilter(statuses);
      activeFilters.current = { ...activeFilters.current, statuses };
      getListings(activeFilters.current);
    },
    [getListings]
  );

  const handleEnergyLabelApply = useCallback(
    (energyLabels) => {
      setEnergyLabelsFilter(energyLabels);
      activeFilters.current = { ...activeFilters.current, energyLabels };
      getListings(activeFilters.current);
    },
    [getListings]
  );

  const handleViewChange = useCallback(
    (mode) => {
      listingModeRef.current = mode;
      setListingMode(mode);
      getListings(activeFilters.current);
    },
    [getListings]
  );

  const handleLegendToggle = useCallback(
    (status) => {
      const current = activeFilters.current.statuses ?? [];
      const next = current.includes(status)
        ? current.filter((s) => s !== status)
        : [...current, status];
      handleStatusApply(next);
    },
    [handleStatusApply]
  );

  const statusCounts = useMemo(() => {
    if (!listingCounts) return {};
    const source =
      listingMode === "my" ? listingCounts.assigned : listingCounts.all;
    if (!source) return {};
    return {
      available: (source.for_sale || 0) + (source.available || 0),
      sale_pending:
        (source.under_bid || 0) +
        (source.under_option || 0) +
        (source.sale_pending || 0),
      sold: (source.sold || 0) + (source.sold_under_reservation || 0),
    };
  }, [listingCounts, listingMode]);

  return (
    <div className="wl-page-root">
      <WelocateHeader
        selectedCity={selectedCity}
        onCitySelect={setSelectedCity}
        listingMode={listingMode}
        onViewChange={handleViewChange}
        myCount={
          listingCounts
            ? Object.values(listingCounts.assigned).reduce((a, b) => a + b, 0)
            : 0
        }
        allCount={
          listingCounts
            ? Object.values(listingCounts.all).reduce((a, b) => a + b, 0)
            : 0
        }
        refetch={refetch}
        onSelectListing={selectListingExpanded}
        aiOpenRequest={aiOpenRequest}
        filters={{
          bedroom: bedroomFilter,
          area: areaFilter,
          types: typeFilter,
          statuses: statusFilter,
          energyLabels: energyLabelsFilter,
        }}
        onBedroomApply={handleBedroomApply}
        onAreaApply={handleAreaApply}
        onTypeApply={handleTypeApply}
        onStatusApply={handleStatusApply}
        onEnergyLabelApply={handleEnergyLabelApply}
        uxExperience={uxExperience}
      />
      <div className="wl-content-row">
        <div className="wl-map-wrapper">
          <APIProvider apiKey={API_KEY}>
            <ClusteredMarkers
              listings={listings}
              selectedListing={selectedListing}
              onSelectListing={selectListingFromMap}
              activeStatuses={statusFilter}
              onToggleStatus={handleLegendToggle}
              selectedCity={selectedCity}
            />
          </APIProvider>
          <div className="wl-filters-overlay">
            <WelocateFilters
              onPriceApply={handlePriceApply}
              onTypeApply={handleTypeApply}
              onBedroomApply={handleBedroomApply}
              onAreaApply={handleAreaApply}
              onStatusApply={handleStatusApply}
              onEnergyLabelApply={handleEnergyLabelApply}
              onUxExperienceApply={setUxExperience}
              uxExperienceSelected={uxExperience}
              bedroomSelected={bedroomFilter}
              areaSelected={areaFilter}
              typeSelected={typeFilter}
              statusSelected={statusFilter}
              energyLabelsSelected={energyLabelsFilter}
              statusCounts={statusCounts}
              totalCount={listings.length}
            />
          </div>
          {!selectedListing && compareList.length === 0 && (
            <div className="wl-bottom-right-overlay">
              <WelocateToolbox />
            </div>
          )}
          <WelocateCompareModal
            compareList={compareList}
            onRemove={removeFromCompare}
            onClear={clearCompare}
            onOpenSideBySide={() => setIsSideBySideOpen(true)}
          />
          <WelocateCompareSideBySide
            open={isSideBySideOpen}
            compareList={compareList}
            onClose={() => setIsSideBySideOpen(false)}
            onRemove={removeFromCompare}
          />
        </div>
        <WelocateSidebar
          selectedListing={selectedListing}
          selectedCity={selectedCity}
          onCitySelect={setSelectedCity}
          onClearSelection={clearSelection}
          listingMode={listingMode}
          refetch={refetch}
          totalCount={listings.length}
          expandRequest={expandRequest}
          collapseRequest={collapseRequest}
          selectionSource={selectionSource}
          onBackToAi={backToAi}
          onAddToCompare={addToCompare}
          compareList={compareList}
        />
      </div>
    </div>
  );
};

export default WelocateListings;
